<template>
  <main>
    <div class="container">
      <al-modal
        :heading="$t('connectionSettings.deleteHeading')"
        :close-label="$t('button.cancel')"
        :cta-label="$t('button.delete')"
        name="delete-connection-modal"
        @dismiss="cancelDelete"
        @cta="doDelete">
        <template v-if="appInstanceToDelete">
          <div class="row">
            <div class="col-full margin-bottom-lg">
              <p class="margin-bottom-none">
                {{ getDeviceName(appInstanceToDelete) }}
              </p>
              <p v-if="appInstanceToDelete.agent" class="margin-bottom-none">
                {{ appInstanceToDelete.agent.name }} {{ appInstanceToDelete.agent.version }}
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-full">
              <p>{{ $t('connectionSettings.deleteBody') }}</p>
            </div>
          </div>
        </template>
      </al-modal>
      <al-modal
        :heading="$t('connectionSettings.modalHeader')"
        :cta-label="$t('button.close')"
        name="learn-more-modal">
        <div class="row">
          <div class="col-full align-left">
            <h2 class="heading-md weight-regular">
              {{ $t('connectionSettings.payFaster.heading') }}
            </h2>
            <div class="row">
              <div class="col-full align-left">
                <ul>
                  <li class="list__item--bullet">
                    <p class="h5 color-gray-dark">
                      {{ $t('connectionSettings.payFaster.item1') }}
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            <h2 class="heading-md weight-regular">
              {{ $t('connectionSettings.stayProtected.heading') }}
            </h2>
            <div class="row">
              <div class="col-full align-left">
                <ul>
                  <li class="list__item--bullet">
                    <p class="h5 color-gray-dark">
                      {{ $t('connectionSettings.stayProtected.item1') }}
                    </p>
                  </li>
                  <li class="list__item--bullet">
                    <p class="h5 color-gray-dark">
                      {{ $t('connectionSettings.stayProtected.item2') }}
                    </p>
                  </li>
                  <li class="list__item--bullet">
                    <p class="h5 color-gray-dark">
                      {{ $t('connectionSettings.stayProtected.item3') }}
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </al-modal>
      <div class="row">
        <div class="col-full heading-xl">
          <h1 class="margin-bottom-xs">
            {{ $t('connectionSettings.heading') }}
          </h1>
          <h2 v-if="!appInstancesEmpty" class="p" data-test="connections-subheading">
            {{ $t('connectionSettings.description') }}
          </h2>
        </div>
      </div>
      <div v-if="!appInstancesEmpty" class="row">
        <div class="col-full">
          <al-list card class="list" data-test="connections-list">
            <al-list-item
              v-for="appInstance in appInstances"
              :key="appInstance.id"
              :name="appInstance.id"
              data-test="connection-list-item">
              <div class="row margin-left-none">
                <div class="col">
                  <p class="margin-bottom-xxs">
                    {{ getDeviceName(appInstance) }}
                  </p>
                  <p v-if="appInstance.agent" class="h5 margin-bottom-none">
                    {{ appInstance.agent.name }} {{ appInstance.agent.version }}
                  </p>
                  <p v-if="appInstance.location" class="h5 margin-bottom-none">
                    {{ $i18nData.getCountryName(appInstance.location.countryCode, locale) }}
                  </p>
                  <p v-if="appInstance.lastUsedTime" class="h5">
                    {{ getLocaleDate(appInstance.lastUsedTime) }}
                  </p>
                </div>
              </div>
              <div slot="cta">
                <button
                  v-analytics:click="$EVENTS.DELETE_BUTTON"
                  v-toggle:al-modal="'delete-connection-modal'"
                  :aria-label="getDeleteDeviceAccessibilityLabel(appInstance)"
                  class="btn--secondary margin-bottom-none"
                  data-test="btn-delete-connection"
                  @click="confirmDelete(appInstance)">
                  {{ $t('button.delete') }}
                </button>
              </div>
            </al-list-item>
          </al-list>
        </div>
      </div>

      <div v-else class="row row-vertical-align-center" data-test="connections-empty-state">
        <div class="col-full align-center">
          <i class="h2 icon-connections icon-size-lg" />
          <h3>
            {{ $t('connectionSettings.emptyHeading') }}
          </h3>
          <i18n path="connectionSettings.addConnection" tag="p" class="p color-secondary">
            <span class="bold color-gray-darkest" place="rememberMe">{{
              $t('connectionSettings.rememberMe')
            }}</span>
            <button
              v-toggle:al-modal="'learn-more-modal'"
              :aria-label="$t('accessibility.connectionsLearnMore')"
              class="btn--secondary"
              type="button"
              place="learnMore">
              {{ $t('connectionSettings.learnMore') }}
            </button>
          </i18n>
          <p class="color-secondary">
            {{ $t('connectionSettings.onceConnect') }}
          </p>
        </div>
      </div>

      <div ref="listBottom" class="row row-vertical-align-bottom">
        <div class="col-full align-center">
          <al-loader v-if="loading" name="connections-loader" dancing-dots />
        </div>
        <div class="col-full align-center">
          <back-button v-analytics:click="$EVENTS.GO_BACK_BUTTON" :location="backButtonRoute">
            {{ $t('button.goBack') }}
          </back-button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { AlList, AlListItem, AlLoader, AlModal } from '@mc-alberta/alcapone'
import throttle from 'lodash.throttle'
import { mapActions, mapGetters, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import { LOADERS, ROUTES } from '@/constants'

const scrollElement = window

export default {
  name: ROUTES.CONNECTION_SETTINGS,
  components: { AlList, AlListItem, AlLoader, AlModal, BackButton },
  data() {
    return {
      backButtonRoute: { name: ROUTES.SETTINGS },
      loading: false,
      appInstanceToDelete: null
    }
  },
  computed: {
    ...mapState({
      callAppInstances: function (state) {
        if (!state.user.callAppInstances) {
          scrollElement.removeEventListener('scroll', this.checkListBottom)
          scrollElement.removeEventListener('wheel', this.checkListBottom)
        }
        return state.user.callAppInstances
      }
    }),
    ...mapGetters({
      appInstances: 'sortedAppInstances',
      locale: 'locale',
      serviceErrorResponse: 'serviceErrorResponse'
    }),
    appInstancesEmpty() {
      return !this.appInstances || !this.appInstances.length
    }
  },
  created() {
    this.checkListBottom = throttle(this.checkListBottom, 250)
  },
  mounted() {
    // use wheel to detect when a user scrolls without a scrollbar
    if (this.callAppInstances) {
      scrollElement.addEventListener('scroll', this.checkListBottom)
      scrollElement.addEventListener('wheel', this.checkListBottom)

      // we need to do this on next tick because if the page was previously scrolled to
      // the bottom on route enter, we would load additional connections without need
      this.$nextTick(() => {
        this.checkListBottom()
      })
    }
  },
  beforeDestroy() {
    this.clearServiceLayerError()

    if (this.callAppInstances) {
      scrollElement.removeEventListener('scroll', this.checkListBottom)
      scrollElement.removeEventListener('wheel', this.checkListBottom)
    }
  },
  methods: {
    ...mapActions([
      'loadAppInstances',
      'deleteAppInstance',
      'startLoader',
      'stopLoader',
      'stopLoaderWithSuccess',
      'setMessagePageContent',
      'clearServiceLayerError'
    ]),
    getDeviceName(appInstance) {
      const { model, brand, manufacturer } = appInstance.device || {}
      return model || brand || manufacturer || this.$t('connectionSettings.unknownDevice')
    },
    getLocaleDate(date) {
      const dateString = date.toLocaleString(this.locale, {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
      const timeString = date.toLocaleString(this.locale, {
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      })

      return this.$t('connectionSettings.timestamp', { date: dateString, time: timeString })
    },
    confirmDelete(appInstance) {
      this.appInstanceToDelete = appInstance
    },
    cancelDelete() {
      this.$analytics.track(this.$EVENTS.MODAL_CANCEL_BUTTON)
      this.appInstanceToDelete = null
    },
    async doDelete() {
      if (!this.appInstanceToDelete) {
        // prevents a race condition issue where the user clicks delete while another delete is processing
        return
      }

      this.$analytics.track(this.$EVENTS.MODAL_DELETE_BUTTON)
      this.startLoader(LOADERS.UPDATE_PROFILE)

      await this.deleteAppInstance(this.appInstanceToDelete.id)
      if (this.serviceErrorResponse()) return this.handleError()

      this.appInstanceToDelete = null
      this.stopLoaderWithSuccess(LOADERS.UPDATE_PROFILE)
    },
    handleError() {
      this.setMessagePageContent({
        buttonTarget: ROUTES.CONNECTION_SETTINGS
      })
      this.$router.push({
        name: ROUTES.ERROR
      })
      this.stopLoader(LOADERS.UPDATE_PROFILE)
    },
    async checkListBottom(event) {
      if (this.loading || (event && event.deltaY <= 0)) return

      const isBottomOfListVisible = this.isBottomOfListVisible()
      if (isBottomOfListVisible) {
        await this.loadMoreAppInstances()
      }
    },
    isBottomOfListVisible() {
      const bounding = this.$refs.listBottom?.getBoundingClientRect()
      if (!bounding) return false

      const boundingInViewport =
        bounding.top >= 0 &&
        bounding.top <= (window.innerHeight || document.documentElement.clientHeight)

      return boundingInViewport
    },
    async loadMoreAppInstances() {
      if (!this.callAppInstances) {
        return
      }

      this.loading = true
      await this.loadAppInstances()
      this.loading = false
    },
    getDeleteDeviceAccessibilityLabel(appInstance) {
      const itemLabel = `${this.getDeviceName(appInstance)}, ${this.getLocaleDate(
        appInstance.lastUsedTime
      )}`

      return this.$t('accessibility.deleteItem', { label: itemLabel })
    }
  }
}
</script>
